import React, { useState, useEffect, useMemo, useRef } from "react";
import { useTable } from "@refinedev/antd";
import { NextActivityCell, useNextActivity } from "@/components/history/NextActivity";
import {
  Input,
  Table,
  Button,
  theme,
  type TableColumnsType,
  Form,
  Grid,
  Tag,
  Flex,
  Typography,
  message,
  Dropdown,
  Drawer,
} from "antd";
import { CrudFilters, HttpError, useGetIdentity } from "@refinedev/core";
import { ContactsFilters } from "./contactsFilters";
import {
  PlusOutlined,
  ShopOutlined,
  DownloadOutlined,
  ContactsOutlined,
  MailOutlined,
  WhatsAppOutlined,
  TeamOutlined,
  MoreOutlined,
  MinusOutlined,
  CloseOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { LateralModal } from "@/components/modals/LateralModal";
import { PaginationTotal } from "@/components/pagination-total";
import {
  axiosInstance,
  displayEmail,
  filterUrlGenerate,
} from "@/utilities";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { utils } from "xlsx";
import {
  API_BASE_URL,
  API_URL_CONTACTS,
  API_URL_SELLERS,
  API_URL_STORES,
} from "@/services/XCONS_ENV";
import { useSelectedStores } from "@/config/context/SelectedItemsContext";
import { toOperatorsFilterValue } from "@/components";
import { EmptyFormCard, FilterTags } from "@/components";
import { useTranslation } from "react-i18next";
import { TFunction, i18n as I18nType } from "i18next";
import { ContactsCreate } from "./contactsCreate";
import { ImportContacts } from "./importsContacts";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ChannelPickerDropdown, channelIcon, useWritableChannels } from "@/components/channel-picker";
import { GroupsCreate } from "./groupsCreate";
import { StoreFilter } from "@/components/select-store-filter";
import { GroupsEdit } from "./groupsEdit";
import { getOperatorTags } from "./utils/operatorTags";
import {
  filterOutOperators,
  useOperatorContacts,
} from "@/hooks/useOperatorContacts";
import { FILAS_POR_PAGINA, OPCIONES_POR_PAGINA } from "@/config/paginacion";

const { Title, Text, Link } = Typography;
const { Search } = Input;
type View = "contacts" | "groups";

const ContactChannelPickerRow = ({ record }: { record: any }) => {
  const navigate = useNavigate();
  const storeCode = record?.storeCode;
  const { channels, unavailable, loading, failed, load } = useWritableChannels(
    storeCode,
    record?.user?.whatsapp,
    { iniciaConPlantilla: true },
  );
  const name = `${record?.user?.firstname ?? ""} ${record?.user?.lastname ?? ""}`.trim();

  const handlePick = (channelId: string) => {
    navigate(
      `/conversations?newMsg=msg&channelId=${encodeURIComponent(channelId)}&contactId=${record?.contactId}&contactName=${encodeURIComponent(name)}&whatsapp=${encodeURIComponent(record?.user?.whatsapp ?? "")}&email=${encodeURIComponent(record?.user?.email ?? "")}&storeCode=${encodeURIComponent(storeCode ?? "")}`,
    );
  };

  const handleAddChannel = () => navigate(`/stores/channels/${storeCode}`);
  const email = record?.user?.email ?? "";
  const whatsapp = record?.user?.whatsapp ?? "";

  return (
    // El desplegable se dibuja afuera de la tabla, pero en React su click sube igual hasta la fila, y
    // la fila abre la ficha: elegir un canal llevaba a la ficha en vez de a la conversación.
    <Flex gap={8} onClick={(e) => e.stopPropagation()}>
      {displayEmail(email) && (
        <ChannelPickerDropdown
          channels={channels}
          loading={loading}
          failed={failed}
          unavailable={unavailable}
          onPick={handlePick}
          onOpen={load}
          onAddChannel={handleAddChannel}
          extraItems={[{
            key: "__email_personal",
            label: <Flex align="center" gap={8} style={{ padding: "4px 0", minWidth: 220 }}>{channelIcon("email")}<span>email personal</span></Flex>,
            onClick: () => { window.location.href = `mailto:${email}`; },
          }]}
        >
          <Button
            style={{ fontSize: 12, padding: 0, fontWeight: 600 }}
            type="link"
            size="small"
            icon={<MailOutlined />}
            onClick={(e) => e.stopPropagation()}
          >
            {displayEmail(email)}
          </Button>
        </ChannelPickerDropdown>
      )}
      {whatsapp && (
        <ChannelPickerDropdown
          channels={channels}
          loading={loading}
          failed={failed}
          unavailable={unavailable}
          onPick={handlePick}
          onOpen={load}
          onAddChannel={handleAddChannel}
          emptyText="No hay canales disponibles para este store"
          extraItems={[{
            key: "__wa_personal",
            label: <Flex align="center" gap={8} style={{ padding: "4px 0", minWidth: 220 }}><WhatsAppOutlined style={{ fontSize: 16 }} /><span>WhatsApp Web personal</span></Flex>,
            onClick: () => { window.open(`https://wa.me/${whatsapp.replace(/[^\d]/g, "")}`, "_blank"); },
          }]}
        >
          <Button
            style={{ fontSize: 12, fontWeight: 600 }}
            type="link"
            size="small"
            icon={<WhatsAppOutlined />}
            onClick={(e) => e.stopPropagation()}
          >
            {whatsapp}
          </Button>
        </ChannelPickerDropdown>
      )}
    </Flex>
  );
};

export const ContactsList: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const [storesSelectProps, setStoresSelectProps] = useState<any>();
  const [sellersSelectProps, setSellersSelectProps] = useState<any>();
  const [showAllMap, setShowAllMap] = useState<Record<string, boolean>>({});
  const [loadingDownload, setLoadingDownload] = useState<boolean>(false);
  const [localData, setLocalData] = useState<any>([]);
  const [selectedContact, setSelectedContact] = useState<any>();
  const screens = Grid.useBreakpoint();
  const { selectedStores, addStore, removeStore, clearStores } =
    useSelectedStores();
  const [searchParams, setSearchParams] = useSearchParams();
  const viewParam = searchParams.get("view");
  const [view, setView] = useState<View>(
    viewParam === "groups" ? "groups" : "contacts",
  );
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [isGroupDrawerOpen, setIsGroupDrawerOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<any | null>(null);
  const [userContactId, setUserContactId] = useState<number[] | null>(null);

  // Kept in refs so onSearch (whose closure may be captured by useTable) always
  // reads the current store/view when composing the store filter.
  const selectedStoresRef = useRef(selectedStores);
  const viewRef = useRef(view);
  useEffect(() => {
    selectedStoresRef.current = selectedStores;
  }, [selectedStores]);
  useEffect(() => {
    viewRef.current = view;
  }, [view]);

  const { token } = theme.useToken();
  const { t, i18n }: { t: TFunction; i18n: I18nType } = useTranslation();
  const { data: identity } = useGetIdentity<{ id?: number }>();
  const operatorContactIds = useOperatorContacts();

  /**
   * El filtro de participación con el que se llegó por URL — así navega "Clientes relacionados"
   * del dashboard (QA #273.8).
   *
   * Vive en estado y **no en el formulario**, y esa es la parte que costó encontrar:
   * `ContactsFilters` está dentro de un drawer con `destroyOnClose`, así que mientras no se abre
   * sus `Form.Item` no existen. `form.submit()` resuelve con `validateFields()`, que devuelve sólo
   * los campos **registrados** — de modo que `participant_toggle` jamás llega a `onSearch` con el
   * drawer cerrado, por más `setFieldsValue` que se le haga. Por eso aplicarlo a mano sí andaba: ahí
   * el drawer está abierto y el campo existe.
   *
   * Se limpia cuando el operador elige "Todos" en el drawer (`onParticipacionLimpiada`).
   */
  const [participacionDeUrl, setParticipacionDeUrl] = useState<any>(() => {
    const qs = new URLSearchParams(window.location.search);
    for (const [k, v] of qs.entries()) {
      const m = k.match(/^filters\[(\d+)\]\[field\]$/);
      if (!m || v !== "participations.contactId") continue;
      const i = m[1];
      const uno = qs.get(`filters[${i}][value]`);
      const varios = [
        ...qs.getAll(`filters[${i}][value][]`),
        ...qs.getAll(`filters[${i}][value][0]`),
      ];
      if (uno !== null && Number.isFinite(Number(uno))) {
        return { field: "participations.contactId", operator: "eq", value: Number(uno) };
      }
      if (varios.length) {
        return {
          field: "participations.contactId",
          operator: "in",
          value: varios.map(Number),
        };
      }
    }
    return null;
  });
  const participacionDeUrlRef = useRef(participacionDeUrl);
  participacionDeUrlRef.current = participacionDeUrl;

  /**
   * El filtro de Operadores **aplicado** (xcons-docs#307), serializado como viaja a la API. Con el
   * drawer cerrado el `Form.Item` `operators` no existe (`destroyOnClose`), `onSearch` lo recibe
   * `undefined`, y el `setFilters(..., "replace")` de cualquier submit —el inicial, o uno del
   * buscador principal— pisaría lo aplicado. Se sostiene desde acá: arranca con lo que vino por
   * URL, toma el valor del drawer cada vez que éste manda, y se suelta al cerrar el chip.
   */
  const [operatorsAplicados, setOperatorsAplicados] = useState<string | null>(() => {
    const qs = new URLSearchParams(window.location.search);
    for (const [k, v] of qs.entries()) {
      const m = k.match(/^filters\[(\d+)\]\[field\]$/);
      if (!m || v !== "operators") continue;
      const raw = qs.get(`filters[${m[1]}][value]`);
      if (raw) return raw;
    }
    return null;
  });
  const operatorsAplicadosRef = useRef(operatorsAplicados);
  operatorsAplicadosRef.current = operatorsAplicados;

  const onSearch = (params: any) => {
    const filters: CrudFilters = [];
    const {
      main_search,
      calendar,
      calendar_created,
      contactGroups,
      operators,
      participant_toggle,
      company,
      archived_status,
      filters: existingFilters,
    } = params;

    if (existingFilters) {
      existingFilters.forEach((filter: any) => {
        filters.push(filter);
      });
      if (!existingFilters.some((f: any) => f.field === "operators")) {
        setOperatorsAplicados(null);
      }
    }
    if (operators !== undefined) {
      setOperatorsAplicados(toOperatorsFilterValue(operators ?? []) ?? null);
    }

    // Con el drawer cerrado `participant_toggle` ni siquiera viaja, así que el filtro de la URL se
    // sostiene desde acá. Cuando el operador abre el drawer y elige, su valor manda.
    if (
      (participant_toggle === undefined || participant_toggle === null) &&
      participacionDeUrlRef.current
    ) {
      filters.push(participacionDeUrlRef.current);
    }

    // participant_toggle holds the userContactId (single value or array)
    if (participant_toggle !== undefined && participant_toggle !== null) {
      const ids = Array.isArray(participant_toggle)
        ? participant_toggle
        : [participant_toggle];
      filters.push({
        field: "participations.contactId",
        operator: ids.length === 1 ? "eq" : "in",
        value: ids.length === 1 ? ids[0] : ids,
      });
    }

    filters.push(
      {
        field: "search",
        operator: "contains",
        value: main_search,
      },
      {
        field: "createdAt",
        operator: "between",
        value: calendar_created
          ? [
            dayjs(calendar_created[0])?.format(),
            dayjs(calendar_created[1]).format(),
          ]
          : undefined,
      },
      {
        field: "updatedAt",
        operator: "between",
        value: calendar
          ? [dayjs(calendar[0])?.format(), dayjs(calendar[1]).format()]
          : undefined,
      },
      // Operadores relacionados, por afiliación (xcons-docs#307). El valor va serializado como lo
      // lee `GET /contacts?filter.operators=`; `undefined` cuando no hay nada tildado. Con el drawer
      // cerrado (`operators === undefined`) se sostiene el último aplicado; si el pedido viene de
      // FilterTags (`existingFilters`) y ya trae `operators`, no se duplica; si no lo trae, es que
      // se cerró el chip.
      ...(existingFilters?.some((f: any) => f.field === "operators")
        ? []
        : [
            {
              field: "operators",
              operator: "eq" as const,
              value:
                operators !== undefined
                  ? toOperatorsFilterValue(operators ?? [])
                  : existingFilters
                    ? undefined
                    : (operatorsAplicadosRef.current ?? undefined),
            },
          ]),
      {
        field: "contactGroupRelation.contactGroup.name",
        operator: "in",
        value: contactGroups,
      },
      {
        // Company filter (U3): backend resolves user.company (EAV) to userIds.
        field: "user.company",
        operator: "contains",
        value: company,
      },
    );

    // Archivado (Fase 2): solo pusheamos el filtro cuando NO es el default
    // (active), para no mostrar un chip "archived: false" ruidoso. El backend
    // sin filter.archived ya devuelve solo activos.
    if (archived_status === "archived" || archived_status === "all") {
      filters.push({
        field: "archived",
        operator: "eq",
        value: archived_status === "archived" ? "true" : "all",
      });
    }

    // The store filter is composed here (single source of truth) so it survives
    // together with the search + drawer filters. It re-runs via the effect that
    // submits the form on store/view change. Fixes the previous bug where
    // useStoreFilters' independent setFilters competed with this one and left
    // the store filter without effect.
    const storeCodes = (selectedStoresRef.current ?? [])
      .map((s: any) => s?.code)
      .filter(Boolean);
    filters.push({
      field: "storeCode",
      // `ilike` is used by the groups resource; it's not in refine's operator
      // union, so cast (same as the previous useStoreFilters hook did).
      operator: (viewRef.current === "contacts" ? "in" : "ilike") as any,
      value: storeCodes.length > 0 ? storeCodes : undefined,
    });

    setFilters(filters, "replace");
    return filters;
  };

  const {
    tableQuery: { data, isLoading, isFetching, refetch },
    searchFormProps,
    tableProps,
    filters,
    setFilters,
  } = useTable<HttpError>({
    resource: view === "contacts" ? "contacts" : "contact-groups",
    pagination: {
      pageSize: FILAS_POR_PAGINA,
      mode: "server",
    },
    sorters: {
      mode: "server",
    },
    onSearch: onSearch,
  });

  const isLoadingTable = !data || isFetching;

  const total = data?.total || 0;

  const dataSource = useMemo(
    () => (isLoadingTable ? [] : localData || []),
    [isLoadingTable, localData],
  );

  useEffect(() => {
    if (data?.data) {
      setLocalData(data.data);
    }
  }, [data?.data]);




  const getUserContactId = async () => {

    const storeCodes = (selectedStores ?? [])
      .map((s) => String(s?.code ?? "").trim())
      .filter(Boolean);

    try {
      if (storeCodes.length === 0) {
        // No store selected: fetch all contacts for this user and collect all IDs
        const response = await axiosInstance.get(`${API_URL_CONTACTS}/`, {
          params: { "filter.userId": identity?.id },
        });

        const contacts = Array.isArray(response.data?.data) ? response.data.data : [];
        const contactIds = contacts
          .map((c: any) => Number(c?.contactId))
          .filter(Boolean);

        setUserContactId(contactIds.length > 0 ? contactIds : null);
        return;
      }

      // Store selected: get the single contact for that store
      const response = await axiosInstance.get(`${API_URL_CONTACTS}/?filter.userId=${identity?.id}&filter.storeCode=${storeCodes.join(",")}`);
      const contactId = response.data.data?.[0]?.contactId;
      setUserContactId(contactId ? [Number(contactId)] : null);
    } catch (error) {
      console.error(error);
    }
  };

  const getStores = async () => {
    try {
      const response = await axiosInstance.get(API_URL_STORES);
      const stores = response.data.data;
      setStoresSelectProps(stores);
    } catch (error) {
      console.error(error);
    }
  };


  const getSellers = async () => {
    try {
      const response = await axiosInstance.get(API_URL_SELLERS);
      const sellers = response.data.data;
      setSellersSelectProps(sellers);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getSellers();
    getStores();
  }, []);

  useEffect(() => {
    // `identity` llega asincrónico. Sin esta guarda la request salía con
    // `filter.userId=undefined`, no resolvía ninguna ficha propia, y el toggle "Soy participante"
    // quedaba apagado para siempre — que es lo que hacía perder el filtro de participación al
    // llegar por URL (QA #273.8). Al llegar la identidad el efecto vuelve a correr.
    if (!identity?.id) return;
    getUserContactId();
  }, [identity?.id, selectedStores]);


  const downloadData = async () => {
    setLoadingDownload(true);

    const total = data?.total;

    try {
      const respuesta = await axiosInstance.get(
        `${API_BASE_URL}/contacts/?page=1&limit=${total}&${filterUrlGenerate(
          filters,
        )}`,
      );

      const allData = respuesta.data.data;

      if (!allData || allData?.length === 0) {
        message.error(t("message.noDataFound"));
        return;
      }

      const columnsToRemove = ["userId"];

      const filteredData = allData.map((item: any) => {
        const newItem = { ...item };
        columnsToRemove.forEach((column) => {
          delete newItem[column];
        });
        return newItem;
      });

      const documentName = `contacts_xcons_${new Date().toISOString()}.xlsx`;
      const worksheet = utils.json_to_sheet(filteredData, {
        header: Object.keys(filteredData[0]),
      });
      const workbook = utils.book_new();
      utils.book_append_sheet(workbook, worksheet, "Xcons data");
      XLSX.writeFile(workbook, documentName);

      message.success(t("message.archiveDownload"));
    } catch (error) {
      message.error(t("message.noDataFound"));
      console.error(error);
    } finally {
      setLoadingDownload(false);
    }
  };

  const toggleShowAll = (id: string) => {
    setShowAllMap((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const syncHeights = () => {
    const titleElement = document.querySelector(
      ".title-container",
    ) as HTMLElement;
    const contactsElement = document.querySelector(
      ".contacts-container",
    ) as HTMLElement;
    const storesElement = document.querySelector(
      ".store-container",
    ) as HTMLElement;
    const storesPermissionsElement = document.querySelector(
      ".storesPermissions-container",
    ) as HTMLElement;
    const sellerElement = document.querySelector(
      ".seller-container",
    ) as HTMLElement;
    const sellerPermissionsElement = document.querySelector(
      ".sellerPermissions-container",
    ) as HTMLElement;

    if (
      !titleElement ||
      !contactsElement ||
      !storesElement ||
      !storesPermissionsElement ||
      !sellerElement ||
      !sellerPermissionsElement
    )
      return;

    const contactsHeight = contactsElement.offsetHeight;
    const storesHeight = storesPermissionsElement.offsetHeight;
    const sellerHeight = sellerPermissionsElement.offsetHeight;

    titleElement.style.height = `${contactsHeight}px`;
    storesElement.style.height = `${storesHeight}px`;
    sellerElement.style.height = `${sellerHeight}px`;
  };

  useEffect(() => {
    syncHeights();
  }, [showAllMap]);

  // Next activity (008 F4): programada futura más próxima por contacto.
  const nextAct = useNextActivity(
    "contact",
    (dataSource as any[]).map((r) => r?.contactId).filter(Boolean),
  );

  const contactsColumns: TableColumnsType<any> = [
    {
      title: t("contacts.table.contact"),
      dataIndex: "user.firstname",
      sorter: true,
      fixed: "left",
      width: 360,
      className: "column-vertical-top",
      render: (incrementId, record) => {
        const participationsStore = [...(record.participations || [])]
          .filter((p) => p.participationType === "store")
          .sort((a, b) => a.position - b.position);

        const participationsSeller = [...(record.participations || [])]
          .filter((p) => p.participationType === "seller")
          .sort((a, b) => a.position - b.position);

        return (
          <Flex vertical>
            <Flex
              align="start"
              justify="space-between"
              className="title-container"
              style={{ marginBottom: 25 }}
              gap={4}
              vertical
            >
              <Flex align="center" gap={6}>
                <Text strong style={{ color: token.colorLinkHover }}>
                  {`${record?.user?.firstname} ${record?.user?.lastname}`}
                </Text>
                {record?.archived && <Tag>Archived</Tag>}
              </Flex>

              <ContactChannelPickerRow record={record} />
            </Flex>

            <Flex vertical gap={12}>
              <Flex
                align="center"
                justify="space-between"
                gap={12}
              >
                {/* Mostrar el store + tags de rol Operador */}
                <Flex gap={4} align="center">
                  <ShopOutlined />{" "}
                  <Text style={{ textTransform: "capitalize" }}>
                    {record?.storeCode}
                  </Text>
                </Flex>
                {(() => {
                  const { isStoreOperator, isSellerOperator } =
                    getOperatorTags(record);
                  if (!isStoreOperator && !isSellerOperator) return null;
                  return (
                    <Flex gap={4} align="center" wrap="wrap">
                      {isStoreOperator && (
                        <Tag color="blue" style={{ margin: 0 }}>
                          Store Operator
                        </Tag>
                      )}
                      {isSellerOperator && (
                        <Tag color="green" style={{ margin: 0 }}>
                          Seller Operator
                        </Tag>
                      )}
                    </Flex>
                  );
                })()}
              </Flex>
            </Flex>
          </Flex>
        );
      },
    },
    {
      title: t("contacts.table.relations"),
      dataIndex: "participations",
      sorter: true,
      render: (participations, record) => {

        // Mirror the filters applied by the detail page (RelationsCard):
        //   - linked contact must be active
        //   - linked user must have a name (drops ghost JOIN rows)
        //   - customer bucket requires participationType === "" (rejects
        //     legacy rogue values like "operator" that aren't in the enum)
        // Otherwise the column renders rows that the detail hides, and the
        // user sees phantom operators on contacts that have stale
        // permission_participation rows (BUG-001).
        const isValidParticipation = (p: any) => {
          if (!p?.contact?.isActive) return false;
          const u = p?.contact?.user;
          if (!u) return false;
          return Boolean(
            String(u.firstname ?? "").trim() || String(u.lastname ?? "").trim(),
          );
        };

        const all: any[] = filterOutOperators(
          (Array.isArray(record.participations) ? record.participations : []) as any[],
          operatorContactIds,
        ).filter(isValidParticipation);

        const sortByPosition = (a: any, b: any) => (a?.position ?? 0) - (b?.position ?? 0);

        const uniqByContactId = (arr: any[]) => {
          const m = new Map<string, any>();
          for (const p of arr) {
            const key = String(p?.contact?.contactId ?? p?.contactId ?? p?.id ?? "");
            if (!key) continue;
            if (!m.has(key)) m.set(key, p);
          }
          return Array.from(m.values());
        };

        const participationsStore = all
          .filter((p) => p.participationType === "store")
          .sort((a, b) => a.position - b.position);

        const participationsSeller = all
          .filter((p) => p.participationType === "seller")
          .sort((a, b) => a.position - b.position);

        const participationsCustomer = uniqByContactId(
          all.filter((p: any) => String(p?.participationType ?? "").trim() === ""),
        ).sort(sortByPosition);

        const formatName = (owner: any) =>
          `${owner?.contact?.user?.firstname ?? ""} ${owner?.contact?.user?.lastname ?? ""
          }`;

        const sliceByExpand = (arr: any[]) =>
          arr.slice(0, expandedRows[record.contactId] ? arr.length : 1);

        const showExpandBtn =
          participationsStore.length > 1 ||
          participationsSeller.length > 1 ||
          participationsCustomer.length > 1;

        return (
          <Flex vertical>
            <div className="contacts-container" style={{ marginBottom: 25 }}>
              <Flex justify="space-between" align="start" gap={10}>
                <Text strong style={{ color: "transparent" }}>
                  -
                </Text>
              </Flex>
            </div>

            {participationsStore
              ?.slice(
                0,
                expandedRows[record.contactId] ? participationsStore.length : 1,
              )
              .map((p) => (
                <div key={p.id}>
                  <Text>
                    {formatName(p)}
                    {p.position === 0 && <Text type="secondary"> (Dueño)</Text>}
                  </Text>
                </div>
              ))}

            {participationsSeller
              ?.slice(
                0,
                expandedRows[record.contactId]
                  ? participationsSeller.length
                  : 1,
              )
              .map((p) => (
                <div key={p.id}>
                  <Text>
                    {formatName(p)}
                    {p.position === 0 && <Text type="secondary"> (Dueño)</Text>}
                  </Text>
                </div>
              ))}

            {sliceByExpand(participationsCustomer).map((p) => (
              <div key={p.id}>
                <Text>
                  {formatName(p)}
                </Text>
              </div>
            ))}
          </Flex>
        );
      },
    },
    {
      title: t("contacts.table.groups"),
      dataIndex: "contactGroups",
      width: 260,
      render: (contactGroups, record) => {
        return (
          <Flex vertical style={{ maxWidth: 400 }}>
            <div className="contacts-container" style={{ marginBottom: 50 }}>
              <Flex justify="space-between" align="start" gap={10}>
                <Text strong style={{ color: "transparent" }}>
                  -
                </Text>
              </Flex>
            </div>

            <Flex
              className="contacts-container"
              style={{ marginBottom: 25 }}
              wrap="wrap"
              gap={4}
            >
              {contactGroups?.map((contactGroup: any, idx: number) => (
                <Flex gap={4} key={idx}>
                  <Tag icon={<TeamOutlined />}>{contactGroup.name}</Tag>
                </Flex>
              ))}
            </Flex>
          </Flex>
        );
      },
    },
    // {
    //   title: t("contacts.table.conversation"),
    //   dataIndex: "contacts",
    //   width: 150,
    //   render: (conversations, record) => {
    //     return (
    //       <Flex vertical>
    //         <div className="contacts-container" style={{ marginBottom: 25 }}>
    //           <Flex justify="space-between" align="start" gap={10}>
    //             <Text strong style={{ color: "transparent" }}>
    //               -
    //             </Text>
    //           </Flex>
    //         </div>

    //         <div className="contacts-container" style={{ marginBottom: 25 }}>
    //           <Flex justify="space-between" align="start" gap={10}>
    //             <Text type="secondary" italic>
    //               {t("contacts.table.noConversations")}
    //             </Text>
    //           </Flex>
    //         </div>
    //       </Flex>
    //     );
    //   },
    // },
    // {
    //   title: t("quotes"),
    //   dataIndex: "quotes",
    //   sorter: (a: any, b: any) => {
    //     const getNumericValue = (value: any) => {
    //       if (!value) return 0;
    //       const num = parseFloat(value);
    //       return isNaN(num) ? 0 : num;
    //     };

    //     const totalDiff =
    //       getNumericValue(a.quoteTotal) - getNumericValue(b.quoteTotal);
    //     if (totalDiff !== 0) return totalDiff;

    //     return getNumericValue(a.quoteCount) - getNumericValue(b.quoteCount);
    //   },
    //   width: 150,
    //   render: (_, record) => {
    //     return (
    //       <Flex vertical>
    //         <div className="contacts-container" style={{ marginBottom: 25 }}>
    //           <Flex justify="space-between" align="start" gap={10}>
    //             <Text strong style={{ color: "transparent" }}>
    //               -
    //             </Text>
    //           </Flex>
    //         </div>
    //         <div className="contacts-container" style={{ marginBottom: 25 }}>
    //           <Flex justify="space-between" align="start" gap={10}>
    //             <Text>
    //               {record?.quoteCount} x{" "}
    //               {formatPrice(record?.quoteTotal) || "-"}
    //             </Text>
    //           </Flex>
    //         </div>
    //       </Flex>
    //     );
    //   },
    // },
    // {
    //   title: t("contacts.table.orders"),
    //   dataIndex: "orders",
    //   sorter: (a, b) => (a.ordersTotal || 0) - (b.ordersTotal || 0),
    //   width: 150,
    //   render: (orders, record) => {
    //     return (
    //       <Flex vertical>
    //         <div className="contacts-container" style={{ marginBottom: 25 }}>
    //           <Flex justify="space-between" align="start" gap={10}>
    //             <Text strong style={{ color: "transparent" }}>
    //               -
    //             </Text>
    //           </Flex>
    //         </div>
    //         <div className="contacts-container" style={{ marginBottom: 25 }}>
    //           <Flex justify="space-between" align="start" gap={10}>
    //             <Text>
    //               {record?.ordersCount} x{" "}
    //               {formatPrice(record?.ordersTotal) || "-"}
    //             </Text>
    //           </Flex>
    //         </div>
    //       </Flex>
    //     );
    //   },
    // },
    // {
    //   title: t("contacts.table.location"),
    //   sorter: (a, b) => {
    //     const addressA = a.address || {};
    //     const addressB = b.address || {};
    //     return (
    //       (addressB.region || "").localeCompare(addressA.region || "") ||
    //       (addressB.city || "").localeCompare(addressA.city || "")
    //     );
    //   },
    //   dataIndex: "address",
    //   width: 300,
    //   render: (address, record) => {
    //     return (
    //       <Flex vertical>
    //         <div className="contacts-container" style={{ marginBottom: 25 }}>
    //           <Flex justify="space-between" align="start" gap={10}>
    //             <Text strong style={{ color: "transparent" }}>
    //               -
    //             </Text>
    //           </Flex>
    //         </div>
    //         <div className="contacts-container" style={{ marginBottom: 25 }}>
    //           <Flex justify="space-between" align="start" gap={10}>
    //             {address ? (
    //               <Text type="secondary" italic>
    //                 {address?.region}, {address?.city}
    //               </Text>
    //             ) : (
    //               <Text type="secondary" italic>
    //                 -
    //               </Text>
    //             )}
    //           </Flex>
    //         </div>
    //       </Flex>
    //     );
    //   },
    // },
    {
      // Empresa (U1). El valor sigue viniendo en `user.company` —el backend resuelve ahí el de la
      // ficha, con el mismo overlay que el nombre—, pero la columna real es `contact.company`.
      //
      // `key` existe por eso: refine arma el campo del sort como `columnKey ?? field`, así que sin
      // él mandaría `user.company`, que del lado del server no es ordenable (la Empresa dejó de
      // ser de la identidad). Con `key` manda `company`, que es la columna indexada.
      //
      // El sort estaba deshabilitado con un comentario que decía que la Empresa era un atributo
      // del EAV. Era cierto: vivía en la base de Magento y ordenar por ella habría exigido un JOIN
      // cross-database. Ya no.
      title: "Compañía",
      dataIndex: ["user", "company"],
      key: "company",
      sorter: true,
      width: 150,
      render: (_company: any, record: any) => {
        return (
          <Flex vertical>
            <div className="contacts-container" style={{ marginBottom: 25 }}>
              <Flex justify="space-between" align="start" gap={10}>
                <Text strong style={{ color: "transparent" }}>
                  -
                </Text>
              </Flex>
            </div>
            <div className="contacts-container" style={{ marginBottom: 25 }}>
              <Flex justify="space-between" align="start" gap={10}>
                {/*
                  Sin Empresa la celda va VACÍA, no con un guión. Y el guión no se borra: se
                  vuelve transparente, que es el mismo recurso que usa esta tabla nueve veces —
                  incluido el renglón de arriba de esta misma celda. Un `<Text>` sin contenido
                  colapsa la altura, y estas celdas son dos renglones apilados que tienen que
                  alinear con las demás columnas.

                  La Empresa está vacía en casi todas las fichas y va a seguir así: la razón
                  social que se carga de verdad vive en la dirección de facturación. O sea que
                  esta columna es mayormente huecos, y una grilla llena de guiones se lee peor
                  que una en blanco.
                */}
                <Text style={record?.user?.company ? undefined : { color: "transparent" }}>
                  {record?.user?.company || "-"}
                </Text>
              </Flex>
            </div>
          </Flex>
        );
      },
    },
    {
      title: t("contacts.table.created"),
      dataIndex: "createdAt",
      sorter: true,
      width: 150,
      render: (createdAt, record) => {
        return (
          <Flex vertical>
            <div className="contacts-container" style={{ marginBottom: 25 }}>
              <Flex justify="space-between" align="start" gap={10}>
                <Text strong style={{ color: "transparent" }}>
                  -
                </Text>
              </Flex>
            </div>
            <div className="contacts-container" style={{ marginBottom: 25 }}>
              <Flex justify="space-between" align="start" gap={10}>
                <Text>{dayjs(createdAt).format("DD/MM/YY")}</Text>
              </Flex>
            </div>
          </Flex>
        );
      },
    },
    {
      title: t("contacts.table.updated"),
      dataIndex: "updatedAt",
      sorter: true,
      width: 150,
      render: (updatedAt, record) => {
        return (
          <Flex vertical>
            <div className="contacts-container" style={{ marginBottom: 25 }}>
              <Flex justify="space-between" align="start" gap={10}>
                <Text strong style={{ color: "transparent" }}>
                  -
                </Text>
              </Flex>
            </div>
            <div className="contacts-container" style={{ marginBottom: 25 }}>
              <Flex justify="space-between" align="start" gap={10}>
                <Text>{dayjs(updatedAt).format("DD/MM/YY")}</Text>
              </Flex>
            </div>
          </Flex>
        );
      },
    },
    // #145: "Próxima actividad" va última en la tabla de Contactos.
    {
      title: "Próxima actividad",
      key: "nextActivity",
      width: 140,
      render: (_: unknown, record: any) => (
        <NextActivityCell value={nextAct[String(record?.contactId)]} />
      ),
    },
  ];

  const groupsColumns: TableColumnsType<any> = [
    {
      title: t("contacts.groupsTable.group"),
      dataIndex: "name",
      sorter: (a, b) =>
        String(a.name).localeCompare(String(b.name), "es", {
          sensitivity: "base",
        }),
      render: (name, record) => {
        return (
          <Flex>
            <Tag icon={<TeamOutlined />}>{name}</Tag>
          </Flex>
        );
      },
    },
    {
      title: t("contacts.groupsTable.linkedStore"),
      dataIndex: "storeCode",
      sorter: (a, b) =>
        String(a.storeCode).localeCompare(String(b.storeCode), "es", {
          sensitivity: "base",
        }),
      render: (storeCode, record) => {
        return (
          <Flex>
            <Text style={{ textTransform: "capitalize" }}>
              <ShopOutlined /> {storeCode}
            </Text>
          </Flex>
        );
      },
    },
    // {
    //   title: t("contacts.groupsTable.automations"),
    //   dataIndex: "name",
    //   width: 200,
    //   render: (firstname, record) => {
    //     return (
    //       <Flex vertical>
    //         <Text>-</Text>
    //       </Flex>
    //     );
    //   },
    // },
    {
      title: t("contacts.groupsTable.contacts"),
      dataIndex: "contactGroupId",
      sorter: (a: any, b: any) =>
        (a?.contactGroupRelation?.length ?? 0) -
        (b?.contactGroupRelation?.length ?? 0),
      render: (contactGroupId, record) => {
        return (
          <Flex>
            <Text>{record?.contactGroupRelation?.length || 0}</Text>
          </Flex>
        );
      },
    },
    {
      title: t("contacts.groupsTable.date"),
      dataIndex: "createdAt",
      sorter: (a, b) =>
        String(a.createdAt).localeCompare(String(b.createdAt), "es", {
          sensitivity: "base",
        }),
      render: (createdAt, record) => {
        return (
          <Flex vertical>
            <Text>{dayjs(createdAt).format("DD/MM/YY")}</Text>
          </Flex>
        );
      },
    },
  ];

  interface HeaderRowProps {
    total?: number;
  }

  const onViewChange = (value: View) => {
    setView(value);
    searchFormProps.form?.resetFields();
    setSearchParams({ view: value });
  };

  const viewOptions = [
    {
      key: "contacts",
      label: t("contacts.viewOptions.contacts"),
    },
    {
      key: "groups",
      label: t("contacts.viewOptions.groups"),
    },
  ];

  const TopHeader = React.memo(() => (
    <Flex gap={12} align="center" style={{ marginBottom: 24 }}>
      <Title level={3} style={{ marginBottom: 0 }}>
        {view === "contacts" ? (
          <>
            <ContactsOutlined /> {t("contacts.title.contacts")}
          </>
        ) : (
          <>
            <TeamOutlined /> {t("contacts.title.groups")}
          </>
        )}
      </Title>
      <Dropdown
        trigger={["click"]}
        menu={{
          items: viewOptions,
          onClick: ({ key }) => onViewChange(key as View),
        }}
      >
        <Button icon={<MoreOutlined />} />
      </Dropdown>
      <StoreFilter stores={storesSelectProps} />
    </Flex>
  ));

  const HeaderRow = React.memo(({ total = 0 }: HeaderRowProps) => (
    <div>
      <Flex flex="block" gap="large" wrap={screens.sm ? false : true}>
        <Form {...searchFormProps} style={{ width: "100%" }}>
          <Form.Item name="main_search">
            <Search
              placeholder={
                view === "contacts"
                  ? t("contacts.search.placeholderContacts")
                  : t("contacts.search.placeholderGroups")
              }
              loading={isLoading}
              allowClear
              onSearch={(value) => {
                if (value.trim() === "") {
                  searchFormProps.form?.setFieldsValue({
                    main_search: undefined,
                  });
                }
                searchFormProps.form?.submit();
              }}
            />
          </Form.Item>
        </Form>

        <Flex
          gap="large"
          justify="center"
          style={{ width: screens.sm ? "auto" : "100%" }}
        >
          {view === "contacts" ? (
            <LateralModal
              buttonName={t("buttons.filters")}
              title={t("filter.title.addFilter")}
            >
              <ContactsFilters
                onParticipacionLimpiada={() => setParticipacionDeUrl(null)}
                searchFormProps={searchFormProps}
                filters={filters}
                storesSelectProps={storesSelectProps}
                selectedStores={selectedStores}
                sellersSelectProps={sellersSelectProps}
                setSelectedContact={setSelectedContact}
                userContactId={userContactId}
                t={t}
              />
            </LateralModal>
          ) : null}

          <Button
            loading={loadingDownload}
            onClick={() => downloadData()}
            icon={<DownloadOutlined />}
            iconPosition="end"
          >
            {t("actions.export")}
          </Button>

          {view === "contacts" ? (
            <LateralModal
              key="imports-modal"
              maskClosable={false}
              width={"50%"}
              buttonType="default"
              buttonShape="default"
              iconSvg={<UploadOutlined />}
              buttonName={t("buttons.import")}
              strong
              title="Importar contactos"
            >
              <ImportContacts defaultStoreCode={(selectedStores?.[0] as any)?.code} />
            </LateralModal>
          ) : null}

          {view === "contacts" ? (
            <LateralModal
              key="filters-modal"
              buttonName={t("contacts.buttons.newContact")}
              title={t("contacts.smartMenu.newContact")}
              buttonType="primary"
              buttonShape="default"
              iconPosition="end"
              iconSvg={<PlusOutlined />}
            >
              <ContactsCreate
                t={t}
                formProps={searchFormProps}
                storesSelectProps={storesSelectProps}
                refetch={refetch}
              />
            </LateralModal>
          ) : (
            <LateralModal
              key="filters-modal"
              buttonName={t("contacts.buttons.newGroup")}
              title={t("contacts.smartMenu.newGroup")}
              buttonType="primary"
              buttonShape="default"
              iconPosition="end"
              iconSvg={<PlusOutlined />}
            >
              <GroupsCreate
                t={t}
                formProps={searchFormProps}
                storesSelectProps={storesSelectProps}
                refetch={refetch}
              />
            </LateralModal>
          )}
        </Flex>
      </Flex>

      <FilterTags
        searchFormProps={searchFormProps}
        onSearch={onSearch}
        i18n={i18n}
        filters={filters}
        setFilters={setFilters}
        t={t}
        selectedContact={selectedContact}
        userContactId={userContactId}
      />

      <Flex justify={total > 0 ? "space-between" : "end"}>
        {total > 0 && (
          <Text style={{ color: token.colorTextSecondary }}>
            {total} {view === "contacts" ? "contacts" : "groups"}
          </Text>
        )}
      </Flex>
    </div>
  ));

  // Re-run the search whenever the selected store(s) or the view change, so the
  // store filter is recomposed together with the search + drawer filters via
  // onSearch (single writer). Replaces the old useStoreFilters, whose
  // independent setFilters competed with onSearch and left the store filter
  // without effect.
  useEffect(() => {
    searchFormProps.form?.submit();
  }, [selectedStores, view]);

  const dataEmpty = false;

  if (dataEmpty) {
    return (
      <>
        <TopHeader />
        <EmptyFormCard
          message={"You don't have any contacts in your list yet."}
          loading={isFetching}
        >
          <LateralModal
            buttonName={t("contacts.buttons.newContact")}
            title={t("contacts.smartMenu.newContact")}
            buttonType="primary"
            buttonShape="default"
            iconSvg={<PlusOutlined />}
          >
            <ContactsCreate t={t} formProps={searchFormProps} />
          </LateralModal>
        </EmptyFormCard>
      </>
    );
  }

  const navigate = useNavigate();

  const handleRowClick = (record: any) => {
    navigate(`/contacts/${record?.contactId}`, {
      state: { record: record },
    });
  };

  return (
    <>
      <TopHeader />
      {view === "contacts" ? (
        <Table
          {...tableProps}
          style={{ cursor: "pointer" }}
          scroll={{ x: "max-content", y: 55 * 10.5 }}
          rowKey="orderId"
          title={() => <HeaderRow total={total} />}
          components={{ header: { cell: (props: any) => <th {...props} style={{ ...props.style, textAlign: "left", height: 60, padding: "0 8px", verticalAlign: "middle" }} /> }, body: { cell: (props: any) => <td {...props} style={{ ...props.style, padding: 8 }} /> } }}
          onRow={(record) => ({
            onClick: () => {
              handleRowClick(record);
            },
          })}
          dataSource={dataSource}
          columns={contactsColumns}
          loading={isLoadingTable}
          locale={{
            triggerAsc: t("table.triggerAsc"),
            triggerDesc: t("table.triggerDesc"),
            cancelSort: t("table.cancelSort"),
          }}
          pagination={{
            ...tableProps.pagination,
            showSizeChanger: true,
            pageSizeOptions: OPCIONES_POR_PAGINA,
            locale: {
              items_per_page: t("table.itemsPerPage"),
              prev_page: t("table.prevPage"),
              next_page: t("table.nextPage"),
              jump_to: t("table.jumpTo"),
              jump_to_confirm: t("table.jumpToConfirm"),
              page: t("table.page"),
              next_5: t("table.next5"),
              prev_5: t("table.prev5"),
            },
            showTotal: (total) => {
              return (
                <PaginationTotal total={total} entityName={t("contacts")} />
              );
            },
          }}
        />
      ) : (
        <>
          <Table
            {...tableProps}
            rowKey="contactGroupId"
            style={{ cursor: "pointer" }}
            title={() => <HeaderRow total={total} />}
            scroll={{ x: "max-content", y: 55 * 10.5 }}
            components={{ header: { cell: (props: any) => <th {...props} style={{ ...props.style, textAlign: "left", height: 60, padding: "0 8px", verticalAlign: "middle" }} /> }, body: { cell: (props: any) => <td {...props} style={{ ...props.style, padding: 8 }} /> } }}
            dataSource={dataSource}
            columns={groupsColumns}
            loading={isLoadingTable}
            onRow={(record) => ({
              onClick: () => {
                setSelectedGroup(record);
                setIsGroupDrawerOpen(true);
              },
            })}
            locale={{
              triggerAsc: t("table.triggerAsc"),
              triggerDesc: t("table.triggerDesc"),
              cancelSort: t("table.cancelSort"),
            }}
            pagination={{
              ...tableProps.pagination,
              showSizeChanger: true,
              pageSizeOptions: OPCIONES_POR_PAGINA,
              locale: {
                items_per_page: t("table.itemsPerPage"),
                prev_page: t("table.prevPage"),
                next_page: t("table.nextPage"),
                jump_to: t("table.jumpTo"),
                jump_to_confirm: t("table.jumpToConfirm"),
                page: t("table.page"),
                next_5: t("table.next5"),
                prev_5: t("table.prev5"),
              },
              showTotal: (total) => {
                return (
                  <PaginationTotal total={total} entityName={t("groups")} />
                );
              },
            }}
          />
        </>
      )}
      <Drawer
        open={isGroupDrawerOpen}
        onClose={() => setIsGroupDrawerOpen(false)}
        title={t("contacts.title.groups")}
        placement="right"
        width={400}
        destroyOnClose
        styles={{
          body: {
            background: "#fff",
          },
          header: {
            display: "none",
          },
        }}
      >
        {selectedGroup ? (
          <Flex vertical gap={12}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                justifyItems: "baseline",
                width: "100%",
              }}
            >
              <Title level={4}>Edit group </Title>
              <Button type="text" onClick={() => setIsGroupDrawerOpen(false)}>
                <CloseOutlined />
              </Button>
            </div>

            <GroupsEdit
              setIsGroupDrawerOpen={setIsGroupDrawerOpen}
              refetch={refetch}
              storesSelectProps={storesSelectProps}
              selectedGroup={selectedGroup}
              t={t}
            />
          </Flex>
        ) : (
          <Text type="secondary">-</Text>
        )}
      </Drawer>
      {children}
    </>
  );
};
